import { useState } from 'react';
import { CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import type { ReviewSessionSummary } from '@/types/api';

interface ApproveConfirmDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly session: ReviewSessionSummary;
  readonly onConfirm: (notes?: string) => void;
  readonly isLoading: boolean;
  readonly hasConflict?: boolean;
}

export function ApproveConfirmDialog({
  open,
  onOpenChange,
  session,
  onConfirm,
  isLoading,
  hasConflict = false,
}: ApproveConfirmDialogProps) {
  const [notes, setNotes] = useState('');

  const handleConfirm = () => {
    onConfirm(notes.trim() || undefined);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-teal" />
            Approve Document Update
          </DialogTitle>
          <DialogDescription>
            You are approving changes to{' '}
            <span className="font-medium text-foreground">
              {session.documentTitle}
            </span>
            . This will queue the document for publishing.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {hasConflict && (
            <div className="flex items-start gap-3 rounded-md border border-amber-500/30 bg-amber-500/5 p-3">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
              <p className="text-sm text-amber-300">
                This document has a regulatory conflict. Your approval
                acknowledges this conflict.
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="approve-notes">Approval notes (optional)</Label>
            <Textarea
              id="approve-notes"
              placeholder="Add context for the audit trail..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            className="bg-teal text-white hover:bg-teal/90"
            onClick={handleConfirm}
            disabled={isLoading}
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Approve
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
